/**
 * Reset Password Page
 * Allows existing users to set a new password after clicking password reset link
 * Flow: Password reset email → /auth/callback → here (for registered users)
 */

import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { Input, Button } from '../shared/components/ui'
import { PasswordStrengthIndicator } from '../components/PasswordStrengthIndicator'
import { supabase } from '../lib/supabase'
import { LocalStorageItem } from '../common/constants'
import logo from '../logo.jpg'

interface FormData {
  password: string
  confirmPassword: string
}

interface FormErrors {
  password?: string
  confirmPassword?: string
  submit?: string
}

/**
 * Validate password form data
 */
function validateForm(formData: FormData): FormErrors {
  const errors: FormErrors = {}

  // Password validation
  if (!formData.password) {
    errors.password = 'Password is required'
  } else {
    const passwordRequirements = [
      { met: formData.password.length >= 8, message: 'Password must be at least 8 characters' },
      { met: /[a-z]/.test(formData.password), message: 'Password must contain a lowercase letter' },
      { met: /\d/.test(formData.password), message: 'Password must contain a number' },
    ]

    const unmetRequirement = passwordRequirements.find((req) => !req.met)
    if (unmetRequirement) {
      errors.password = unmetRequirement.message
    }
  }

  // Confirm password validation
  if (!formData.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password'
  } else if (formData.password !== formData.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match'
  }

  return errors
}

/**
 * Reset Password Page Component
 */
export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [formData, setFormData] = useState<FormData>({
    password: '',
    confirmPassword: '',
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  /**
   * Check if form is valid for submission
   */
  const isFormValid = (): boolean => {
    if (!formData.password) return false
    if (!formData.confirmPassword) return false

    // Check password meets requirements
    const passwordValid =
      formData.password.length >= 8 &&
      /[a-z]/.test(formData.password) &&
      /\d/.test(formData.password)

    if (!passwordValid) return false

    // Check passwords match
    if (formData.password !== formData.confirmPassword) return false

    return true
  }

  // Load email from Supabase session
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession()

      if (session?.user?.email) {
        console.log('Session found for password reset')
        setEmail(session.user.email)
        return
      }

      // No session - redirect to forgot password
      console.error('No active session, redirecting to forgot password')
      navigate('/forgot-password', {
        replace: true,
        state: { message: 'Please request a new password reset link.' }
      })
    }

    checkAuth()
  }, [navigate])

  /**
   * Handle input changes
   */
  function handleInputChange(field: keyof FormData, value: string) {
    const newFormData = { ...formData, [field]: value }
    setFormData(newFormData)

    // Clear error for this field when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }

    // Real-time password mismatch validation
    if (field === 'confirmPassword' && value) {
      if (newFormData.password !== value) {
        setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }))
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: undefined }))
      }
    }

    // Also check when password field changes and confirm password has a value
    if (field === 'password' && newFormData.confirmPassword) {
      if (value !== newFormData.confirmPassword) {
        setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }))
      } else {
        setErrors((prev) => ({ ...prev, confirmPassword: undefined }))
      }
    }
  }

  /**
   * Handle form submission
   */
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    // Client-side validation
    const validationErrors = validateForm(formData)
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setSubmitting(true)
    setErrors({})

    try {
      // Update password in Supabase Auth
      const { error: passwordError } = await supabase.auth.updateUser({
        password: formData.password,
      })

      if (passwordError) {
        console.error('Failed to update password:', passwordError)
        throw new Error(passwordError.message || 'Failed to update password')
      }

      console.log('Password updated successfully')

      // Completing a password reset means this account is fully set up, so any
      // registrationPending flag left over from an earlier flow is stale. It
      // would otherwise make the very next login bounce straight back here.
      localStorage.removeItem(LocalStorageItem.registrationPending)

      // Sign out to force fresh login with new password
      await supabase.auth.signOut()

      // Redirect to login with success message
      navigate('/login', {
        replace: true,
        state: { message: 'Password updated successfully. Please sign in with your new password.' }
      })
    } catch (error: any) {
      console.error('Password reset failed:', error)

      if (error?.message) {
        setErrors({ submit: error.message })
      } else {
        setErrors({ submit: 'Failed to update password. Please try again.' })
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (!email) {
    return null // Will redirect in useEffect
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 py-12 px-4">
      <div className="w-full max-w-md">
        {/* Reset Password Card */}
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Logo and Branding */}
          <div className="text-center mb-8">
            <div className="mb-2 flex justify-center">
              <img src={logo} alt="IoAg" className="h-16" />
            </div>
            <p className="text-gray-600 text-sm">IoT Agriculture Monitoring</p>
          </div>

          {/* Heading */}
          <h2 className="text-2xl font-semibold text-center text-gray-900 mb-2">
            Reset Your Password
          </h2>
          <p className="text-center text-gray-600 text-sm mb-6">
            Enter your new password below
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email (Read-only) */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                disabled
                className="w-full bg-gray-50"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your new password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  error={!!errors.password}
                  className="w-full pr-10"
                  disabled={submitting}
                  autoFocus
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {errors.password && <p className="text-xs text-red-600 mt-1">{errors.password}</p>}
            </div>

            {/* Password Strength Indicator */}
            {formData.password && (
              <div className="bg-gray-50 rounded-lg p-4">
                <PasswordStrengthIndicator password={formData.password} />
              </div>
            )}

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter your new password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  error={!!errors.confirmPassword}
                  className="w-full pr-10"
                  disabled={submitting}
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {errors.confirmPassword && <p className="text-xs text-red-600 mt-1">{errors.confirmPassword}</p>}
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                {errors.submit}
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-2">
              <Button
                type="submit"
                disabled={submitting || !isFormValid()}
                loading={submitting}
                variant="primary"
                className="w-full"
              >
                {submitting ? 'Updating Password...' : 'Update Password'}
              </Button>
            </div>
          </form>
        </div>

        {/* Help Text */}
        <div className="text-center mt-4">
          <p className="text-sm text-gray-600">
            <Link to="/login" className="text-blue-600 hover:text-blue-800 hover:underline">
              Back to Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
